import { useTranslation } from 'react-i18next'

import { BorderRadius, fontSizes } from '@enechain/ecloud-designsystem'

import {
  AutoBidSideLine,
  type SideRow,
} from '~/pages/AdminApprovalFlow/components/AutoBidCellSummary'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'

type Props = {
  /**
   * 代表エリアの買い / 売り。 **undefined = 保存済みルールを持つ自動エリアが
   * 1 つも無い**状態で、 その時は既定値サマリではなく「未設定」 を出す
   * (AC-056b / 鉄則2: システム既定値のまま自動発注される見た目を作らない)。
   */
  rows: { buy: SideRow; sell: SideRow } | undefined
}

/**
 * 一括セレクト = `自動` (マトリクス非表示) の時に出す、 保存済みルールの 2 行サマリ
 * (AC-APF-060)。
 *
 * セル内サマリ (AC-APF-047 / [[AutoBidCellSummary]]) と **同じ行コンポーネントを通す**
 * ので、 書式 (`カバー率{N}% / {発注内容}`) と bid-limits 実効値の⚠が 2 か所で割れない。
 * 違いは「ルール編集」 ボタンを持たないことだけで、 編集導線は一括セレクト脇の
 * 「全エリア一括編集」 が担う (design `admin/approval-flow.html` も同じ分担)。
 */
export const AutoBidBulkSummary = ({ rows }: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        // design `.apr-collapsed-rule-summary`: 買い行 / 売り行は 1 つの塊として
        // 読ませるので行間を詰める (セル内サマリの 4px より狭い 2px)。
        gap: '2px',
        // 枠 + 地を入れて「いま効いているルール」 の塊を地の白から分離する
        // (デザイナー指摘 2026-09-14「ルール保存後、 現在のルール記載の視認性が
        // 悪い」)。 マトリクスが畳まれている間、 この 2 行が設定値の唯一の表示
        // なので、 周囲の説明文と同じ地に置くと拾えない。
        //
        // 枠・地・角丸はセル内サマリ ([[AutoBidCellSummary]]) の買/売チップと
        // 同じトークン (`Border.lowEmphasis` / `Surface.secondary` / `radiiBase`)
        // を使う。 同じ情報の別表示なので、 ここで新しい見た目を作らない。
        //
        // 未設定の時も同じ枠に入れる。 保存後だけ枠が付くと、 保存の瞬間に
        // 高さが跳ねて視認性の問題を別の問題に置き換えることになる。
        border: `1px solid ${eclColor.borderLow}`,
        borderRadius: BorderRadius.radiiBase.value,
        background: eclColor.surfaceSecondary,
        padding: '8px 12px',
        // 内容分だけ囲う。 行いっぱいに伸ばすと 2 行の塊が帯に見えて
        // 「値」 ではなく「注意書き」 として読まれる。
        width: 'fit-content',
      }}
    >
      {rows === undefined ? (
        <span
          style={{
            fontSize: fontSizes.sm.value,
            // design `.apr-collapsed-rule-unset`。 セル内の未設定 (保存ブロック要因)
            // と違い、 ここは「まだ何も保存していない」 という状態の報告なので
            // 警告色にしない。 促しは隣の「全エリア一括編集」 ボタンが担う。
            color: eclColor.textMid,
          }}
        >
          {t('page:adminApprovalFlow.autoBid.bulkRuleUnset')}
        </span>
      ) : (
        <>
          {/* 「現在設定中のルール」 見出し (デザイナー指摘 2026-09-14
              「ルールを 1 度保存した後、 『→』 アイコンの右に『現在設定中のルール』 の
              見出しと合わせて現在のルールを配置」)。 **保存済みの時だけ**出す —
              未設定の分岐に付けると「現在設定中のルール: 未設定です」 という
              自己矛盾した見出しになる。 */}
          <span
            style={{
              // デザイナー指摘 2026-09-14:「『現在設定中のルール』フォント一つ上げる」
              // (xs → sm)。 本文の買/売行と同じ段になり、 見出しだけ沈まない。
              fontSize: fontSizes.sm.value,
              fontWeight: 600,
              color: eclColor.textMid,
            }}
          >
            {t('page:adminApprovalFlow.autoBid.bulkRuleHeading')}
          </span>
          <AutoBidSideLine
            row={rows.buy}
            label={t('page:adminApprovalFlow.autoBid.side.buy')}
            side="buy"
          />
          <AutoBidSideLine
            row={rows.sell}
            label={t('page:adminApprovalFlow.autoBid.side.sell')}
            side="sell"
          />
        </>
      )}
    </div>
  )
}
